import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  Inject,
  inject,
  Input,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { toSignal } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { MatIconModule } from '@angular/material/icon';
import { ResponsiveService } from '../../../shared/services/responsive.service';
import { PSIC_COMPONENTS } from '@pacificspecialtyinsurancecompany/psic-ng-components';
import { globalMessages } from '../../../shared/global-message';
import { formatAmount, isDateExpired } from '../../../shared/utils';
import { StepUpAuthService } from '../../../shared/services/step-up-auth.service';
import { APP_CONFIG, AppConfig } from '../../../../app.config';
import { CardLoaderComponent } from '../../../shared/components/card-loader/card-loader.component';
import { ToastService } from '../../../shared/services/toast.service';
import { MakePaymentVisibilityService } from '../../../shared/services/make-payment-visibility.service';
import { DownloadService } from '../../../shared/services/download.service';
import { PortletService } from '../services/portlet.service';
import { UserAccessService } from '../../../shared/services/user-access.service';

@Component({
  selector: 'app-current-statement',
  standalone: true,
  imports: [CommonModule, MatIconModule, ...PSIC_COMPONENTS, CardLoaderComponent],
  templateUrl: './current-statement.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CurrentStatementComponent {
  @Input() dcBillingFailed: any = false;
  globalMessages = globalMessages;

  private readonly responsive = inject(ResponsiveService);
  private readonly store = inject(Store);
  private readonly router = inject(Router);
  private readonly stepUpAuth = inject(StepUpAuthService);
  private readonly toastService = inject(ToastService);
  private readonly makePaymentVisibility = inject(MakePaymentVisibilityService);
  private readonly downloadService = inject(DownloadService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly portletService = inject(PortletService);
  readonly userAccess = inject(UserAccessService);

  readonly isMobile = toSignal(this.responsive.isMobile$, { initialValue: false });

  private readonly policyNumber = this.stepUpAuth.getPolicyNumber();

  private readonly storeData = toSignal(this.store.select((state: any) => state.policy.data));
  readonly isLoading = toSignal(
    this.store.select((state: any) => state.policy.loading),
    { initialValue: true }
  );

  readonly currentStatementData = computed(() => {
    const entry = this.storeData()?.[this.policyNumber!];
    const billingSnapshot = entry?.billingSnapshot;
    if (!billingSnapshot) return null;
    return {
      PolicyReference: billingSnapshot?.policyInfo?.policyNumber,
      CurrentDueAmount: billingSnapshot?.currentStatement?.amountToBePaidTodayInclusiveOfPastDue,
      DueDate: billingSnapshot?.currentStatement?.dueDate,
      InvoiceNumber: billingSnapshot?.currentStatement?.invoiceReference,
      pastDueAmount: billingSnapshot?.currentStatement?.pastDueAmount,
      OutstandingBalance: billingSnapshot?.currentStatement?.accountOutstandingBalance,
      PolicyStatus: entry?.policySnapshot?.policyInfo?.status ?? '',
      AttachmentId: billingSnapshot?.currentStatement?.invoiceAttachmentId,
    };
  });

  readonly isDateExpired = computed(() => {
    const dueDate = this.currentStatementData()?.DueDate;
    return dueDate ? isDateExpired(dueDate) : false;
  });

  readonly policyStatusTheme = computed(() => {
    const status = this.currentStatementData()?.PolicyStatus?.toLowerCase() ?? '';
    if (status === globalMessages.policyStatusInforce) return globalMessages.tagThemeNeutralAq;
    if (status === globalMessages.policyStatusExpired) return globalMessages.tagThemeError;
    if (
      status === globalMessages.policyStatusCancelled ||
      status === globalMessages.policyStatusCancelPending
    )
      return globalMessages.tagThemeDefault;
    if (status === globalMessages.policyStatusNew) return globalMessages.tagThemeNeutralPurple;
    return globalMessages.tagThemeDefault;
  });

  constructor(@Inject(APP_CONFIG) private config: AppConfig) {
    effect(() => {
      this.makePaymentVisibility.setVisibility(this.displayBottomSection());
    });
  }

  navigateToMakePayment(): void {
    if (this.userAccess.hasEditableAccess()) {
      if (this.userAccess.hasFullAccess()) {
        this.portletService.updateAudit('RedirectToMakePayment');
        this.router.navigate([globalMessages.routeMakePayment], {
          queryParamsHandling: 'preserve',
        });
      } else {
        let guestPayUrl =
          this.config.guestPayDomainURL +
          globalMessages.guestPayMakePaymentURL +
          '?Category=PolicyNumber&search=' +
          this.stepUpAuth.getPolicyNumber() +
          '&Zipcode=' +
          this.stepUpAuth.getZipcode();
        this.portletService.updateAudit('RedirectToGPP');
        window.open(guestPayUrl, '_blank');
      }
    }
  }

  formatAmount(amount?: string): string {
    return formatAmount(amount);
  }

  readonly displayBottomSection = computed(() => {
    if (!this.userAccess.hasEditableAccess) {
      return false;
    }

    const data = this.currentStatementData();
    if (!data) return false;
    const dueAmount = data.CurrentDueAmount;
    const balanceDue = data.OutstandingBalance;
    // Hide bottom section if DueAmount is present and value is 0
    // or if BalanceDue is present and value is 0
    if (
      (dueAmount === undefined && balanceDue === undefined) ||
      (dueAmount === undefined && balanceDue !== undefined && Number(balanceDue) === 0) ||
      (dueAmount !== undefined && Number(dueAmount) === 0 && balanceDue === undefined) ||
      (dueAmount !== undefined &&
        Number(dueAmount) === 0 &&
        balanceDue !== undefined &&
        Number(balanceDue) === 0)
    ) {
      return false;
    }
    return true;
  });

  downloadInvoice(attachmentId: string) {
    if (attachmentId) {
      this.downloadService.download(
        'downloadInvoice',
        { attachmentId },
        'document.pdf',
        this.destroyRef
      );
    } else {
      this.toastService.add({
        type: 'error',
        title: 'Error',
        message: 'Failed to download document. Please try again.',
      });
    }
  }
}
